import {
  type QueryClient,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { ApiError, api } from "@/lib/api";
import { ME_QUERY_KEY, type MeResponse } from "@/lib/auth";
import { type Candado, candadosQueryKey } from "@/lib/candados";
import { dashboardQueryKey } from "@/lib/dashboard";
import { type Equipo, equiposQueryKey } from "@/lib/equipos";
import { useListaFiltrada } from "@/lib/filtros";
import { type EquipoDeArea, type FiltrosLotos, filtrarLotos } from "@/lib/filtros/lotos";
import {
  MENSAJE_RETIRO_AJENO_OFFLINE,
  OfflinePolicyError,
  puedeRetirarSinConexion,
} from "@/lib/offline-policy";
import { getOnline } from "@/lib/online-status";
import {
  enqueue,
  enqueueAttachment,
  getLocalRef,
  listAll,
  nextTempId,
  type OutboxMutation,
  type OutboxRef,
  putLocalRef,
  subscribeOutbox,
  uuidv7,
} from "@/lib/outbox";
import { type Procedimiento, procedimientosQueryKey } from "@/lib/procedimientos";
import { type Punto, puntosQueryKey } from "@/lib/puntos";

/**
 * EstadoLoto es el estado del ciclo de vida del LOTO, BINARIO desde #241: apertura
 * (ABIERTO) → cierre (CERRADO). La única transición es el cierre, que exige que no queden
 * candados activos. Los estados son muy visuales en la ficha y en el panel (#43).
 */
export type EstadoLoto = "ABIERTO" | "CERRADO";

/**
 * LotoListItem es una fila del índice de LOTOs (GET /api/v1/lotos): incluye los activos
 * y el histórico de cerrados, ordenados del más reciente al más antiguo. `bloqueos_activos`
 * es el número de candados colgados ahora mismo (0 salvo que haya aislamiento en curso).
 *
 * Desde #250 la fila dice QUIÉN abrió el LOTO y quién lo cerró, cada uno con su id y su
 * nombre (D4): el nombre es lo que pinta la columna y el id es lo que casa el filtro
 * `creado_por`, que el servidor aplica por id y la función pura replica sin red por ese
 * mismo id, nunca por texto. Nombre vacío = su usuario ya no existe (D6, la UI pinta «—»);
 * los dos campos de cierre llegan vacíos mientras el LOTO siga ABIERTO (D2).
 */
export type LotoListItem = {
  id: number;
  numero_loto: string;
  estado: EstadoLoto;
  id_equipo: number;
  equipo: string;
  codigo_equipo: string;
  area: string;
  descripcion: string | null;
  bloqueos_activos: number;
  fecha_apertura: string;
  creado_por: number | null;
  creado_por_nombre: string;
  fecha_cierre: string | null;
  cerrado_por: number | null;
  cerrado_por_nombre: string;
};

/**
 * PuntoDeBloqueo es uno de los puntos de aislamiento que cubre un candado de la ficha
 * (#244: un candado puede cubrir varios). `activo` false ⇒ ese punto ya se liberó.
 * `retirado_por_nombre` queda "" si el usuario que lo retiró se borró.
 */
export type PuntoDeBloqueo = {
  id_punto: number;
  nombre: string;
  descripcion: string;
  activo: boolean;
  retirado_por: number | null;
  retirado_por_nombre: string;
  fecha_retiro: string | null;
};

/**
 * PuntoDeCandado es la proyección de un punto cubierto en la RESPUESTA de colocar/retirar
 * (POST /lotos/{id}/bloqueos y /bloqueos/{id}/retirar). Desde #246 el punto tiene UN solo
 * nombre en toda la pila, así que la ficha y esta respuesta comparten vocabulario.
 */
export type PuntoDeCandado = {
  id_punto: number;
  nombre: string;
  descripcion: string;
  activo: boolean;
  retirado_por: number | null;
  fecha_retiro: string | null;
};

/**
 * BloqueoDetalle es un candado colocado sobre uno o varios puntos de aislamiento dentro de un
 * LOTO (#244), con el contexto legible (puntos, operario que lo colocó, quién lo retiró).
 * `activo` false ⇒ ya retirado (FechaRetiro fijada). `num_fotos` son las fotos de
 * verificación adjuntas.
 */
export type BloqueoDetalle = {
  id: number;
  puntos: PuntoDeBloqueo[];
  id_candado: number;
  codigo_candado: string;
  etiqueta: string | null;
  observaciones: string | null;
  colocado_por: number | null;
  operario: string;
  fecha_colocacion: string;
  retirado_por: number | null;
  retirado_por_nombre: string;
  fecha_retiro: string | null;
  num_fotos: number;
  activo: boolean;
};

/**
 * textoPuntos compone la etiqueta legible de los puntos que cubre un candado
 * («Interruptor general · Seccionador, Válvula de purga · Corte»), para pies de foto y
 * textos de una sola línea. Vive aquí, junto al tipo, para que la ficha y el registro
 * fotográfico no la dupliquen.
 */
export function textoPuntos(bloqueo: BloqueoDetalle): string {
  return bloqueo.puntos.map((p) => `${p.nombre} · ${p.descripcion}`).join(", ") || "—";
}

/**
 * Loto es la representación base de un LOTO (la devuelve el alta POST /api/v1/lotos y es
 * el núcleo del detalle). `estado` gobierna las transiciones disponibles en la ficha.
 */
export type Loto = {
  id: number;
  numero_loto: string;
  id_equipo: number;
  id_procedimiento: number | null;
  estado: EstadoLoto;
  descripcion: string | null;
  observaciones: string | null;
  creado_por: number | null;
  fecha_apertura: string;
  cerrado_por: number | null;
  fecha_cierre: string | null;
  version: number;
  updated_at: string;
};

/**
 * ProcedimientoDelLoto es el procedimiento de aislamiento asignado al LOTO tal y como lo
 * proyecta la ficha (#236): la secuencia ORDENADA de cortes y el criterio de verificación de
 * energía cero, para leerlos durante la intervención sin resolver ids contra el catálogo. Es
 * un subconjunto del `Procedimiento` de los maestros (`lib/procedimientos.ts`); el servidor lo
 * devuelve AUNQUE esté desactivado (`activo: false`), porque es el que rigió la intervención.
 */
export type ProcedimientoDelLoto = Pick<
  Procedimiento,
  | "id"
  | "codigo_procedimiento"
  | "version_procedimiento"
  | "pasos"
  | "verificacion_energia_cero"
  | "activo"
>;

/**
 * LotoDetail es la ficha completa (GET /api/v1/lotos/{id}): los campos base más el
 * contexto del equipo y la lista de bloqueos (activos primero, luego por colocación desc).
 */
export type LotoDetail = Loto & {
  equipo: string;
  codigo_equipo: string;
  area: string;
  bloqueos: BloqueoDetalle[];
  // num_fotos es el número de fotos del registro fotográfico del LOTO (#212): el contador de la
  // ficha, para no cargar los metadatos (que van por GET /lotos/{id}/fotos) solo para contarlos.
  num_fotos: number;
  // procedimiento es el bloque del procedimiento de aislamiento (#236), o null si el LOTO no
  // tiene ninguno asignado. Lo resuelve el SERVIDOR: la ficha no lo busca en el catálogo.
  procedimiento: ProcedimientoDelLoto | null;
};

/**
 * FotoLoto es una foto del registro fotográfico de un LOTO (GET /api/v1/obras/{obraId}/lotos/{id}/
 * fotos, #211/#212). El binario NO va aquí: se sirve autenticado por cookie mismo-origen desde
 * `/api/v1/obras/{obraId}/lotos/{id}/fotos/{idFoto}` (usable directo como `<img src>`). `fase` es
 * el estado del LOTO que el SERVIDOR estampa al subir (#241); `subido_por_nombre` es "" si el
 * usuario se borró.
 */
export type FotoLoto = {
  id: number;
  id_loto: number;
  fase: EstadoLoto;
  comentario: string | null;
  mime: string;
  tamano: number;
  hash: string | null;
  subido_por: number | null;
  subido_por_nombre: string;
  created_at: string;
};

/**
 * LotoInput es el payload de apertura (POST /api/v1/lotos). `numero_loto` es opcional: si
 * se deja vacío lo autogenera el servidor (LOTO-YYYY-XXXX). `id_procedimiento`,
 * `descripcion` y `observaciones` son opcionales (ausente ⇒ NULL).
 */
export type LotoInput = {
  id_equipo: number;
  id_procedimiento?: number | null;
  numero_loto?: string;
  descripcion?: string | null;
  observaciones?: string | null;
};

/**
 * BloqueoInput es el payload para colocar un candado sobre uno o varios puntos de aislamiento
 * (POST /api/v1/lotos/{id}/bloqueos, #244). El id del LOTO viaja por la ruta. `etiqueta` y
 * `observaciones` son opcionales. El servidor revalida que todos los puntos existan, estén
 * activos y pertenezcan al equipo del LOTO: es todo o nada.
 */
export type BloqueoInput = {
  id_puntos: number[];
  id_candado: number;
  etiqueta?: string | null;
  observaciones?: string | null;
};

/**
 * BloqueoResponse es la respuesta al colocar o retirar un candado. `fecha_retiro` nil ⇒
 * el bloqueo sigue activo.
 */
export type BloqueoResponse = {
  id: number;
  id_loto: number;
  puntos: PuntoDeCandado[];
  id_candado: number;
  codigo_candado: string;
  etiqueta: string | null;
  observaciones: string | null;
  colocado_por: number | null;
  fecha_colocacion: string;
  retirado_por: number | null;
  fecha_retiro: string | null;
  version: number;
  updated_at: string;
};

/**
 * lotosQueryKey namespacea la caché de LOTOs POR obra: `["obra", obraId, "lotos"]` (F7 #158).
 * La lista cuelga de esta clave y la ficha de un LOTO de `[...lotosQueryKey(obraId), id]`, de
 * modo que los LOTOs (y sus ids temporales negativos) de una obra jamás casan con los de otra.
 */
export function lotosQueryKey(obraId: number) {
  return ["obra", obraId, "lotos"] as const;
}

/**
 * lotosQueryOptions lista los LOTOs de la obra `obraId` (activos + histórico) del más reciente
 * al más antiguo. Se refresca invalidando la clave tras cada transición o cambio de bloqueos.
 */
export function lotosQueryOptions(obraId: number) {
  return queryOptions({
    queryKey: lotosQueryKey(obraId),
    queryFn: () => api.get<LotoListItem[]>(`/obras/${obraId}/lotos`),
  });
}

/** lotoQueryOptions lee la ficha completa de un LOTO. Clave anidada [...lotos(obraId), id]. */
export const lotoQueryOptions = (obraId: number, id: number) =>
  queryOptions({
    queryKey: [...lotosQueryKey(obraId), id],
    queryFn: () => api.get<LotoDetail>(`/obras/${obraId}/lotos/${id}`),
  });

/**
 * useLotos lista el índice de LOTOs de la obra, opcionalmente FILTRADO (#247): con red
 * filtra el servidor; sin red, la función pura sobre la lista base cacheada.
 *
 * `equipos` solo hace falta para el filtro por ÁREA sin conexión: el servidor lo resuelve
 * por `equipos.id_area` y la fila del índice trae el nombre del área, no su id, así que el
 * equivalente exacto en el cliente es el conjunto de equipos de esa área. La página se los
 * pasa desde la lista de equipos que ya carga para el desplegable.
 *
 * Es el único llamante que COMPONE su función de filtrado (los demás pasan la función pura
 * tal cual). Va en un `useCallback` (#285 R7) porque `useListaFiltrada` la memoiza: una
 * lambda nueva en cada render invalidaría ese `useMemo` y el listado sin red se recalcularía
 * —devolviendo un array nuevo— en cada repintado de la página.
 */
export function useLotos(obraId: number, filtros: FiltrosLotos = {}, equipos: EquipoDeArea[] = []) {
  const filtrar = useCallback(
    (filas: LotoListItem[], f: Record<string, string>) => filtrarLotos(filas, f, equipos),
    [equipos],
  );
  return useListaFiltrada<LotoListItem>({
    claveBase: lotosQueryKey(obraId),
    url: `/obras/${obraId}/lotos`,
    filtros,
    filtrar,
  });
}

export function useLoto(obraId: number, id: number) {
  return useQuery(lotoQueryOptions(obraId, id));
}

/**
 * altaRechazadaDe resuelve, para un id TEMPORAL de LOTO (negativo, creado sin red), la mutación de
 * alta que el servidor RECHAZÓ al sincronizar, o `null` si no hay ninguna. El camino es el mismo
 * que usa la cadena causal de #53, en sentido inverso: el id temporal resuelve por `localRefs` la
 * identidad local (`clientUuid`) con la que se encoló el alta, y esa identidad selecciona SU fila
 * en la outbox. Por eso el rechazo de OTRA alta no toca esta ficha.
 */
async function altaRechazadaDe(tempId: number): Promise<OutboxMutation | null> {
  const ref = await getLocalRef(tempId);
  if (ref?.entity !== "loto") {
    return null;
  }
  const filas = await listAll();
  return (
    filas.find(
      (m) =>
        m.entity === "loto" &&
        m.op === "create" &&
        m.clientUuid === ref.clientUuid &&
        m.status === "rejected",
    ) ?? null
  );
}

/**
 * useAltaLotoRechazada observa la outbox y devuelve la mutación del alta de ESTE LOTO si el
 * servidor la rechazó (#306), o `null`. La ficha de un LOTO con id temporal la consulta para
 * cerrarse con un aviso en vez de seguir pintando una intervención que no existe ni existirá; el
 * `resumen` y el `errorCode` de la fila son lo que el aviso nombra y explica.
 *
 * Es reactiva por el pub/sub de la outbox: el veredicto lo escribe el runner en pleno flush, con
 * la ficha ya montada. Para un id de servidor (positivo) responde `null` sin tocar IndexedDB.
 */
export function useAltaLotoRechazada(lotoId: number): OutboxMutation | null {
  const [rechazo, setRechazo] = useState<OutboxMutation | null>(null);

  useEffect(() => {
    if (lotoId >= 0) {
      setRechazo(null);
      return;
    }
    let vivo = true;
    const refrescar = (): void => {
      void altaRechazadaDe(lotoId).then((m) => {
        if (vivo) {
          setRechazo(m);
        }
      });
    };
    refrescar();
    const baja = subscribeOutbox(refrescar);
    return () => {
      vivo = false;
      baja();
    };
  }, [lotoId]);

  return rechazo;
}

// invalidarLoto refresca la lista, la ficha afectada y el panel de la obra: toda transición o
// cambio de candados altera los contadores del dashboard (candados activos, equipos bloqueados).
function invalidarLoto(
  queryClient: ReturnType<typeof useQueryClient>,
  obraId: number,
  idLoto: number,
): void {
  void queryClient.invalidateQueries({ queryKey: lotosQueryKey(obraId) });
  void queryClient.invalidateQueries({ queryKey: [...lotosQueryKey(obraId), idLoto] });
  void queryClient.invalidateQueries({ queryKey: dashboardQueryKey(obraId) });
}

export function useCreateLoto(obraId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    // Online: POST al REST y refresco por invalidación (intacto).
    // Offline: encolar el alta + LOTO optimista con id temporal (política offline aditiva, #53).
    mutationFn: async (input: LotoInput) => {
      if (getOnline()) {
        const loto = await api.post<Loto>(`/obras/${obraId}/lotos`, input);
        invalidarLoto(queryClient, obraId, loto.id);
        return loto;
      }
      return encolarCrearLoto(queryClient, obraId, input);
    },
  });
}

export function useCerrarLoto(obraId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post<Loto>(`/obras/${obraId}/lotos/${id}/cerrar`),
    onSuccess: (loto) => invalidarLoto(queryClient, obraId, loto.id),
  });
}

/**
 * useCambiarProcedimiento cambia el procedimiento asignado a un LOTO ya creado (SPEC-LOTO-004,
 * #129). Es una operación ONLINE (como el cierre): exige la autoridad del servidor para
 * validar que el procedimiento pertenece al equipo y la regla de permiso (creador o supervisor);
 * la UI la deshabilita sin conexión. Refresca la ficha, la lista y el panel por invalidación.
 */
export function useCambiarProcedimiento(obraId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ idLoto, idProcedimiento }: { idLoto: number; idProcedimiento: number }) =>
      api.post<Loto>(`/obras/${obraId}/lotos/${idLoto}/procedimiento`, {
        id_procedimiento: idProcedimiento,
      }),
    onSuccess: (loto) => invalidarLoto(queryClient, obraId, loto.id),
  });
}

// --- Escrituras offline (F4 #52/#53): outbox + update optimista -------------------
//
// Sin red, las operaciones de la allowlist aditiva (crear LOTO, colocar/retirar candado PROPIO;
// ver offline-policy.ts) NO viajan al REST: se encolan en la outbox y su efecto se refleja de
// forma optimista en la caché de lecturas para que el técnico vea el cambio al instante. El runner
// las envía en orden al reconectar y la reconciliación sustituye el estado optimista por el
// autoritativo. El CIERRE (única transición del ciclo binario, #241) sigue siendo online: la UI
// lo deshabilita sin red («Requiere conexión»). El retiro de candado ajeno se prohíbe offline aquí
// mismo (defensa en profundidad: no se encola aunque la UI fallara en deshabilitarlo).
//
// Cadena causal (#53): una entidad creada offline vive con un id temporal NEGATIVO hasta que el
// servidor le asigna el real. Una mutación que la referencia (colocar un candado sobre un LOTO
// recién creado offline) resuelve por `localRefs` la identidad local (clientUuid) de esa entidad y
// encola una `OutboxRef`; el runner reescribe la referencia con el id de servidor cuando el alta se
// aplica. Así el orden FIFO + refs mantiene la dependencia sin conocer los ids reales.

// equipoDeCache lee un equipo de la caché de la obra (para el `resumen` de la mutación y el LOTO
// optimista). Lo que falte degrada a un guion.
function equipoDeCache(
  queryClient: QueryClient,
  obraId: number,
  idEquipo: number,
): Equipo | undefined {
  return (queryClient.getQueryData<Equipo[]>(equiposQueryKey(obraId)) ?? []).find(
    (e) => e.id === idEquipo,
  );
}

// procedimientoDeCache resuelve el procedimiento elegido contra el catálogo cacheado de la
// obra, para que el LOTO optimista offline muestre su bloque en la ficha igual que lo haría el
// servidor (mismo criterio que equipoDeCache). Si no está cacheado, la ficha optimista lo deja
// en null: el bloque autoritativo llegará al reconciliar tras el PUSH.
function procedimientoDeCache(
  queryClient: QueryClient,
  obraId: number,
  idProcedimiento: number | null | undefined,
): ProcedimientoDelLoto | null {
  if (idProcedimiento == null) {
    return null;
  }
  const p = (queryClient.getQueryData<Procedimiento[]>(procedimientosQueryKey(obraId)) ?? []).find(
    (x) => x.id === idProcedimiento,
  );
  return p
    ? {
        id: p.id,
        codigo_procedimiento: p.codigo_procedimiento,
        version_procedimiento: p.version_procedimiento,
        pasos: p.pasos,
        verificacion_energia_cero: p.verificacion_energia_cero,
        activo: p.activo,
      }
    : null;
}

// numeroLotoDeCache lee el número (o placeholder) de un LOTO ya en caché de la obra, para redactar
// el `resumen` de las mutaciones de candado en términos que el técnico reconozca.
function numeroLotoDeCache(queryClient: QueryClient, obraId: number, idLoto: number): string {
  const detalle = queryClient.getQueryData<LotoDetail>([...lotosQueryKey(obraId), idLoto]);
  return detalle?.numero_loto ?? "LOTO";
}

// encolarCrearLoto encola el alta y pinta un LOTO optimista (ABIERTO, sin candados) con id temporal
// NEGATIVO en la lista y su ficha, para que el técnico salte a operarlo al instante. Registra el
// enlace id temporal → clientUuid (localRefs) de modo que un candado colocado sobre este LOTO
// resuelva su referencia al sincronizar. Devuelve el LOTO optimista; el número definitivo lo
// asignará el servidor (numero_loto se envía vacío salvo que el técnico lo fije).
async function encolarCrearLoto(
  queryClient: QueryClient,
  obraId: number,
  input: LotoInput,
): Promise<Loto> {
  const clientUuid = uuidv7();
  const tempId = nextTempId();
  const me = queryClient.getQueryData<MeResponse>(ME_QUERY_KEY);
  const equipo = equipoDeCache(queryClient, obraId, input.id_equipo);
  const numeroFijado = input.numero_loto?.trim();
  const numeroLoto = numeroFijado && numeroFijado.length > 0 ? numeroFijado : "LOTO (sin conexión)";
  const etiquetaEquipo = equipo ? `${equipo.codigo_equipo} · ${equipo.nombre_equipo}` : "el equipo";

  await enqueue({
    idObra: obraId,
    clientMutationId: uuidv7(),
    entity: "loto",
    op: "create",
    clientUuid,
    baseVersion: null,
    payload: {
      id_equipo: input.id_equipo,
      id_procedimiento: input.id_procedimiento ?? null,
      numero_loto: numeroFijado ?? "",
      descripcion: input.descripcion ?? null,
      observaciones: input.observaciones ?? null,
    },
    resumen: `Abrir LOTO en ${etiquetaEquipo}`,
  });
  await putLocalRef(tempId, obraId, "loto", clientUuid);

  const ahora = new Date().toISOString();
  const base: Loto = {
    id: tempId,
    numero_loto: numeroLoto,
    id_equipo: input.id_equipo,
    id_procedimiento: input.id_procedimiento ?? null,
    estado: "ABIERTO",
    descripcion: input.descripcion ?? null,
    observaciones: input.observaciones ?? null,
    creado_por: me?.id ?? null,
    fecha_apertura: ahora,
    cerrado_por: null,
    fecha_cierre: null,
    version: 0,
    updated_at: ahora,
  };
  const detalle: LotoDetail = {
    ...base,
    equipo: equipo?.nombre_equipo ?? "—",
    codigo_equipo: equipo?.codigo_equipo ?? "—",
    area: equipo?.nombre_area ?? "—",
    bloqueos: [],
    num_fotos: 0,
    procedimiento: procedimientoDeCache(queryClient, obraId, input.id_procedimiento),
  };
  const item: LotoListItem = {
    id: tempId,
    numero_loto: numeroLoto,
    estado: "ABIERTO",
    id_equipo: input.id_equipo,
    equipo: detalle.equipo,
    codigo_equipo: detalle.codigo_equipo,
    area: detalle.area,
    descripcion: base.descripcion,
    bloqueos_activos: 0,
    fecha_apertura: ahora,
    // #250 D1: el LOTO abierto sin red enseña al usuario de la SESIÓN desde el primer
    // momento. Es lo único que no miente —quien lo abrió fue él— y al sincronizar el
    // servidor sella ese mismo autor, así que no hay salto visible. El nombre se compone
    // como el `TRIM(nombre || ' ' || apellidos)` del servidor.
    creado_por: me?.id ?? null,
    creado_por_nombre: me ? `${me.nombre} ${me.apellidos}`.trim() : "",
    fecha_cierre: null,
    cerrado_por: null,
    cerrado_por_nombre: "",
  };
  queryClient.setQueryData<LotoDetail>([...lotosQueryKey(obraId), tempId], detalle);
  queryClient.setQueryData<LotoListItem[]>(lotosQueryKey(obraId), (prev) =>
    prev ? [item, ...prev] : [item],
  );
  return base;
}

// nombresDePuntos compone «Interruptor general, Válvula de purga» desde la caché de maestros,
// para el resumen legible que el centro de sincronización muestra por cada mutación encolada.
function nombresDePuntos(queryClient: QueryClient, obraId: number, idPuntos: number[]): string {
  const catalogo = queryClient.getQueryData<Punto[]>(puntosQueryKey(obraId)) ?? [];
  return idPuntos.map((id) => catalogo.find((p) => p.id === id)?.nombre ?? "—").join(", ");
}

// aPuntosDeCandado proyecta los puntos de la ficha a la forma de la RESPUESTA de colocar/retirar,
// para que la respuesta local offline tenga la misma forma que la del servidor.
function aPuntosDeCandado(puntos: PuntoDeBloqueo[]): PuntoDeCandado[] {
  return puntos.map((p) => ({
    id_punto: p.id_punto,
    nombre: p.nombre,
    descripcion: p.descripcion,
    activo: p.activo,
    retirado_por: p.retirado_por,
    fecha_retiro: p.fecha_retiro,
  }));
}

// puntosOptimistas resuelve el contexto legible (nombre y descripción) de los puntos elegidos
// desde la caché de maestros ya hidratada. Lo que falte degrada a un guion; la reconciliación
// traerá los datos reales al sincronizar. Todos nacen ACTIVOS: acaban de cubrirse.
function puntosOptimistas(
  queryClient: QueryClient,
  obraId: number,
  idPuntos: number[],
): PuntoDeBloqueo[] {
  const catalogo = queryClient.getQueryData<Punto[]>(puntosQueryKey(obraId)) ?? [];
  return idPuntos.map((id) => {
    const p = catalogo.find((x) => x.id === id);
    return {
      id_punto: id,
      nombre: p?.nombre ?? "—",
      descripcion: p?.descripcion ?? "",
      activo: true,
      retirado_por: null,
      retirado_por_nombre: "",
      fecha_retiro: null,
    };
  });
}

// construirBloqueoOptimista sintetiza el bloqueo que se pinta en la ficha al colocar sin red,
// leyendo el contexto legible (puntos, candado, operario) de las cachés ya hidratadas. Lo que
// falte degrada a un guion; la reconciliación traerá los datos reales al sincronizar.
function construirBloqueoOptimista(
  queryClient: QueryClient,
  obraId: number,
  tempId: number,
  input: BloqueoInput,
): BloqueoDetalle {
  const candados = queryClient.getQueryData<Candado[]>(candadosQueryKey(obraId)) ?? [];
  const me = queryClient.getQueryData<MeResponse>(ME_QUERY_KEY);
  const candado = candados.find((c) => c.id === input.id_candado);
  return {
    id: tempId,
    puntos: puntosOptimistas(queryClient, obraId, input.id_puntos),
    id_candado: input.id_candado,
    codigo_candado: candado?.codigo_candado ?? "—",
    etiqueta: input.etiqueta ?? null,
    observaciones: input.observaciones ?? null,
    colocado_por: me?.id ?? null,
    operario: me ? `${me.nombre} ${me.apellidos}`.trim() : "",
    fecha_colocacion: new Date().toISOString(),
    retirado_por: null,
    retirado_por_nombre: "",
    fecha_retiro: null,
    num_fotos: 0,
    activo: true,
  };
}

// encolarColocar encola la colocación y refleja el candado activo en la ficha y el contador de
// la lista. Devuelve un BloqueoResponse local con id temporal NEGATIVO (los ids de servidor son
// positivos, así que nunca colisionan), que el wizard consume como cualquier respuesta.
async function encolarColocar(
  queryClient: QueryClient,
  obraId: number,
  idLoto: number,
  input: BloqueoInput,
): Promise<BloqueoResponse> {
  const clientUuid = uuidv7();
  const tempId = nextTempId();

  // Cadena causal: si el LOTO se creó offline (id temporal negativo), la colocación referencia su
  // identidad local en vez de un id que aún no existe en el servidor; el runner reescribe id_loto
  // con el id real tras el id_mapping del alta. Sobre un LOTO ya sincronizado (id positivo) no hace
  // falta ref: el id_loto del payload ya es el definitivo.
  const refs: OutboxRef[] = [];
  if (idLoto < 0) {
    const ref = await getLocalRef(idLoto);
    if (ref) {
      refs.push({ field: "id_loto", entity: ref.entity, clientUuid: ref.clientUuid });
    }
  }
  const candado = (queryClient.getQueryData<Candado[]>(candadosQueryKey(obraId)) ?? []).find(
    (c) => c.id === input.id_candado,
  );

  await enqueue({
    idObra: obraId,
    clientMutationId: uuidv7(),
    entity: "bloqueo",
    op: "create",
    clientUuid,
    baseVersion: null,
    payload: {
      id_loto: idLoto,
      id_puntos: input.id_puntos,
      id_candado: input.id_candado,
      etiqueta: input.etiqueta ?? null,
      observaciones: input.observaciones ?? null,
    },
    refs,
    resumen: `Colocar candado ${candado?.codigo_candado ?? "—"} en ${numeroLotoDeCache(queryClient, obraId, idLoto)} (${nombresDePuntos(queryClient, obraId, input.id_puntos)})`,
  });
  // Identidad local del bloqueo, por si se retira offline antes de sincronizar (retiro por ref).
  await putLocalRef(tempId, obraId, "bloqueo", clientUuid);

  const bloqueo = construirBloqueoOptimista(queryClient, obraId, tempId, input);
  queryClient.setQueryData<LotoDetail>([...lotosQueryKey(obraId), idLoto], (prev) =>
    prev ? { ...prev, bloqueos: [bloqueo, ...prev.bloqueos] } : prev,
  );
  queryClient.setQueryData<LotoListItem[]>(lotosQueryKey(obraId), (prev) =>
    prev?.map((l) => (l.id === idLoto ? { ...l, bloqueos_activos: l.bloqueos_activos + 1 } : l)),
  );

  return {
    id: tempId,
    id_loto: idLoto,
    puntos: aPuntosDeCandado(bloqueo.puntos),
    id_candado: input.id_candado,
    codigo_candado: bloqueo.codigo_candado,
    etiqueta: bloqueo.etiqueta,
    observaciones: bloqueo.observaciones,
    colocado_por: bloqueo.colocado_por,
    fecha_colocacion: bloqueo.fecha_colocacion,
    retirado_por: null,
    fecha_retiro: null,
    version: 0,
    updated_at: bloqueo.fecha_colocacion,
  };
}

// encolarRetirarPunto encola la retirada de UN punto (#245) y refleja el efecto de forma
// optimista: el punto queda retirado y, si era el ÚLTIMO activo del candado, también el candado
// (y el contador de la lista baja), igual que hará el servidor en su transacción. El diálogo
// ignora la respuesta; se devuelve un BloqueoResponse coherente.
async function encolarRetirarPunto(
  queryClient: QueryClient,
  obraId: number,
  idBloqueo: number,
  idPunto: number,
  idLoto: number,
  motivo?: string | null,
): Promise<BloqueoResponse> {
  // Cadena causal: si el candado se colocó offline (id de bloqueo temporal), la retirada
  // referencia su identidad local para que el runner reescriba id_bloqueo con el id real tras
  // sincronizar la colocación. Sobre un bloqueo ya sincronizado (id positivo) el id es firme.
  const refs: OutboxRef[] = [];
  if (idBloqueo < 0) {
    const ref = await getLocalRef(idBloqueo);
    if (ref) {
      refs.push({ field: "id_bloqueo", entity: ref.entity, clientUuid: ref.clientUuid });
    }
  }
  const bloqueoDet = queryClient
    .getQueryData<LotoDetail>([...lotosQueryKey(obraId), idLoto])
    ?.bloqueos.find((b) => b.id === idBloqueo);
  const puntoDet = bloqueoDet?.puntos.find((p) => p.id_punto === idPunto);
  // ¿Es el último punto aún cubierto? De ello depende el optimista del candado. Fail-closed: sin
  // la ficha en caché no se sabe, así que NO se atenúa el candado ni se baja el contador —el
  // servidor es quien decide y la reconciliación traerá el estado real—. Se exige además que el
  // único punto activo sea justo el que se retira: retirar uno ya retirado no libera nada.
  const activos = bloqueoDet?.puntos.filter((p) => p.activo) ?? [];
  const ultimo = activos.length === 1 && activos[0].id_punto === idPunto;

  await enqueue({
    idObra: obraId,
    clientMutationId: uuidv7(),
    entity: "bloqueo",
    op: "retirar_punto",
    clientUuid: "", // la retirada no crea entidad: sin identidad local ni id_mapping
    baseVersion: null,
    payload: { id_bloqueo: idBloqueo, id_punto: idPunto, motivo: motivo ?? null },
    refs,
    resumen: `Retirar el punto ${puntoDet?.nombre ?? "—"} del candado ${bloqueoDet?.codigo_candado ?? "—"} en ${numeroLotoDeCache(queryClient, obraId, idLoto)}`,
  });

  const me = queryClient.getQueryData<MeResponse>(ME_QUERY_KEY);
  const nombre = me ? `${me.nombre} ${me.apellidos}`.trim() : "";
  const ahora = new Date().toISOString();
  queryClient.setQueryData<LotoDetail>([...lotosQueryKey(obraId), idLoto], (prev) =>
    prev
      ? {
          ...prev,
          bloqueos: prev.bloqueos.map((b) =>
            b.id === idBloqueo
              ? {
                  ...b,
                  // El candado solo se atenúa si este era su último punto cubierto.
                  activo: ultimo ? false : b.activo,
                  retirado_por: ultimo ? (me?.id ?? null) : b.retirado_por,
                  retirado_por_nombre: ultimo ? nombre : b.retirado_por_nombre,
                  fecha_retiro: ultimo ? ahora : b.fecha_retiro,
                  puntos: b.puntos.map((p) =>
                    p.id_punto === idPunto && p.activo
                      ? {
                          ...p,
                          activo: false,
                          retirado_por: me?.id ?? null,
                          retirado_por_nombre: nombre,
                          fecha_retiro: ahora,
                        }
                      : p,
                  ),
                }
              : b,
          ),
        }
      : prev,
  );
  if (ultimo) {
    queryClient.setQueryData<LotoListItem[]>(lotosQueryKey(obraId), (prev) =>
      prev?.map((l) =>
        l.id === idLoto ? { ...l, bloqueos_activos: Math.max(0, l.bloqueos_activos - 1) } : l,
      ),
    );
  }

  const b = queryClient
    .getQueryData<LotoDetail>([...lotosQueryKey(obraId), idLoto])
    ?.bloqueos.find((x) => x.id === idBloqueo);
  return {
    id: idBloqueo,
    id_loto: idLoto,
    puntos: aPuntosDeCandado(b?.puntos ?? []),
    id_candado: b?.id_candado ?? 0,
    codigo_candado: b?.codigo_candado ?? "",
    etiqueta: b?.etiqueta ?? null,
    observaciones: b?.observaciones ?? null,
    colocado_por: b?.colocado_por ?? null,
    fecha_colocacion: b?.fecha_colocacion ?? ahora,
    retirado_por: b?.retirado_por ?? null,
    fecha_retiro: b?.fecha_retiro ?? null,
    version: 0,
    updated_at: ahora,
  };
}

export function useColocarBloqueo(obraId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    // Online: POST al REST y refresco por invalidación (comportamiento verificado en F3, intacto).
    // Offline: encolar en la outbox + update optimista.
    mutationFn: async ({ idLoto, input }: { idLoto: number; input: BloqueoInput }) => {
      if (getOnline()) {
        const data = await api.post<BloqueoResponse>(
          `/obras/${obraId}/lotos/${idLoto}/bloqueos`,
          input,
        );
        invalidarLoto(queryClient, obraId, idLoto);
        return data;
      }
      return encolarColocar(queryClient, obraId, idLoto, input);
    },
  });
}

/**
 * useRetirarPunto retira UN punto de aislamiento cubierto por un candado (#245). El candado se
 * libera solo cuando el servidor sella su ÚLTIMO punto, en la misma transacción: aquí no se
 * decide nada de seguridad, solo se envía o se encola la intención.
 *
 * Online: POST a `/bloqueos/{id}/puntos/{idPunto}/retirar` y refresco por invalidación.
 * Offline: guarda de propiedad (la del CANDADO) + encolado en la outbox con update optimista.
 */
export function useRetirarPunto(obraId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      idBloqueo,
      idPunto,
      idLoto,
      motivo,
    }: {
      idBloqueo: number;
      idPunto: number;
      idLoto: number;
      motivo?: string | null;
    }) => {
      if (getOnline()) {
        const data = await api.post<BloqueoResponse>(
          `/obras/${obraId}/bloqueos/${idBloqueo}/puntos/${idPunto}/retirar`,
          motivo ? { motivo } : undefined,
        );
        invalidarLoto(queryClient, obraId, idLoto);
        return data;
      }
      // Offline: solo se permite retirar un punto de un candado PROPIO (el ajeno es un override
      // que exige la autoridad del servidor). Defensa en profundidad: aunque la UI deshabilite el
      // botón para un candado ajeno, aquí se garantiza que una acción prohibida NUNCA se encole.
      const me = queryClient.getQueryData<MeResponse>(ME_QUERY_KEY);
      const bloqueo = queryClient
        .getQueryData<LotoDetail>([...lotosQueryKey(obraId), idLoto])
        ?.bloqueos.find((b) => b.id === idBloqueo);
      if (!puedeRetirarSinConexion(bloqueo?.colocado_por ?? null, me?.id)) {
        throw new OfflinePolicyError(MENSAJE_RETIRO_AJENO_OFFLINE);
      }
      return encolarRetirarPunto(queryClient, obraId, idBloqueo, idPunto, idLoto, motivo);
    },
  });
}

// useSubirFoto adjunta una foto de verificación a un bloqueo (#40/#54). El binario NO viaja por el
// PUSH de sync (el applier solo entiende JSON): o se sube directo por multipart (online) o se ENCOLA
// como adjunto para que el runner lo suba al endpoint POST /bloqueos/{id}/fotos al reconectar. Así la
// evidencia capturada sin red nunca se pierde y queda ligada al bloqueo correcto (por su identidad
// local si se creó offline, o por su id real si ya existía). Devuelve `{ encolada }` para que la UI
// distinga «subida al servidor» de «guardada para sincronizar».
export function useSubirFoto(obraId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      idBloqueo,
      file,
      orden,
    }: {
      idBloqueo: number;
      idLoto: number;
      file: File;
      orden: number;
    }): Promise<{ encolada: boolean }> => {
      const mime = file.type || "image/jpeg";

      // Bloqueo creado offline (id temporal negativo): aún no existe en el servidor. La foto se liga
      // a la identidad local del bloqueo (clientUuid, vía localRefs); el runner la subirá cuando el
      // id_mapping del alta resuelva el id real.
      if (idBloqueo < 0) {
        const ref = await getLocalRef(idBloqueo);
        await enqueueAttachment({
          idObra: obraId,
          clientUuid: ref?.clientUuid ?? null,
          serverId: null,
          blob: file,
          mime,
          orden,
        });
        return { encolada: true };
      }

      // Bloqueo real. Online: subida multipart directa (comportamiento F3 intacto). Un error
      // PERMANENTE de dominio (tope de 2 fotos, MIME/tamaño inválido) se propaga para que el wizard
      // avise; un fallo de red / 401 / 5xx encola la foto ligada al id real, para no perderla si la
      // conexión cae justo después de colocar el candado.
      if (getOnline()) {
        const form = new FormData();
        form.append("foto", file);
        try {
          await api.postForm<unknown>(`/obras/${obraId}/bloqueos/${idBloqueo}/fotos`, form);
          return { encolada: false };
        } catch (err) {
          if (
            err instanceof ApiError &&
            err.status >= 400 &&
            err.status < 500 &&
            err.status !== 401 &&
            err.status !== 429
          ) {
            throw err;
          }
          await enqueueAttachment({
            idObra: obraId,
            clientUuid: null,
            serverId: idBloqueo,
            blob: file,
            mime,
            orden,
          });
          return { encolada: true };
        }
      }

      // Offline con un bloqueo ya real (colocado online, conexión caída después): encolar por id real.
      await enqueueAttachment({
        idObra: obraId,
        clientUuid: null,
        serverId: idBloqueo,
        blob: file,
        mime,
        orden,
      });
      return { encolada: true };
    },
    // Solo la subida ONLINE cambia num_fotos en el servidor: invalida la ficha para refrescar. Las
    // fotos encoladas no tocan el servidor todavía (el runner reconcilia la ficha al subirlas).
    onSuccess: (data, { idLoto }) => {
      if (!data.encolada) {
        void queryClient.invalidateQueries({ queryKey: [...lotosQueryKey(obraId), idLoto] });
      }
    },
  });
}

// --- Registro fotográfico del LOTO (#212) ---------------------------------------------

/**
 * fotosLotoQueryKey namespacea la lista de fotos del registro fotográfico de un LOTO, colgando de
 * la clave de su ficha: `[...lotos(obraId), idLoto, "fotos"]`. Así una invalidación de la ficha por
 * prefijo (la del runner o la de las mutaciones) también refresca sus fotos sin claves sueltas.
 */
export function fotosLotoQueryKey(obraId: number, idLoto: number) {
  return [...lotosQueryKey(obraId), idLoto, "fotos"] as const;
}

/**
 * useFotosLoto lee la lista de metadatos de las fotos del registro fotográfico de un LOTO. El
 * binario de cada foto se sirve aparte (GET .../fotos/{idFoto}) y se pinta con `<img src>` directo
 * (cookie mismo-origen). Deshabilitada para ids temporales (<0): un LOTO creado offline aún no
 * tiene fotos en el servidor.
 */
export function useFotosLoto(obraId: number, idLoto: number) {
  return useQuery({
    queryKey: fotosLotoQueryKey(obraId, idLoto),
    queryFn: () => api.get<FotoLoto[]>(`/obras/${obraId}/lotos/${idLoto}/fotos`),
    enabled: idLoto > 0,
  });
}

/**
 * useSubirFotoLoto sube una foto al registro fotográfico de un LOTO (#212), con el mismo patrón
 * online/offline que useSubirFoto: online va por multipart directo (con el `comentario` opcional;
 * la FASE la sella el servidor, #241); si la red falla (o estamos sin conexión, o el LOTO se creó
 * offline y su id aún es temporal), la foto se ENCOLA como adjunto de LOTO (kind "loto") para que
 * el runner la suba al endpoint POST /lotos/{id}/fotos al reconectar. Devuelve `{ encolada }` para
 * que la UI distinga «subida al servidor» de «guardada para sincronizar». Un error PERMANENTE de
 * dominio (tope de 10 fotos, MIME/tamaño inválidos) se propaga para que la UI lo muestre.
 */
export function useSubirFotoLoto(obraId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      idLoto,
      file,
      comentario,
      orden,
    }: {
      idLoto: number;
      file: File;
      comentario: string | null;
      orden: number;
    }): Promise<{ encolada: boolean }> => {
      const mime = file.type || "image/jpeg";

      // LOTO creado offline (id temporal negativo): aún no existe en el servidor. La foto se liga a
      // la identidad local del LOTO (clientUuid, vía localRefs); el runner la subirá cuando el
      // id_mapping del alta resuelva el id real.
      if (idLoto < 0) {
        const ref = await getLocalRef(idLoto);
        await enqueueAttachment({
          kind: "loto",
          idObra: obraId,
          clientUuid: ref?.clientUuid ?? null,
          serverId: null,
          blob: file,
          mime,
          comentario,
          orden,
        });
        return { encolada: true };
      }

      // LOTO real. Online: subida multipart directa. La FASE no se envía (#241): la sella el
      // servidor con el estado del LOTO. Un error PERMANENTE de dominio (tope de 10 fotos,
      // MIME/tamaño inválidos) se propaga; un fallo de red / 401 / 5xx / 429 encola la foto por
      // id real, para no perderla si la conexión cae justo después de capturarla.
      if (getOnline()) {
        const form = new FormData();
        form.append("foto", file);
        if (comentario) {
          form.append("comentario", comentario);
        }
        try {
          await api.postForm<FotoLoto>(`/obras/${obraId}/lotos/${idLoto}/fotos`, form);
          return { encolada: false };
        } catch (err) {
          if (
            err instanceof ApiError &&
            err.status >= 400 &&
            err.status < 500 &&
            err.status !== 401 &&
            err.status !== 429
          ) {
            throw err;
          }
          await enqueueAttachment({
            kind: "loto",
            idObra: obraId,
            clientUuid: null,
            serverId: idLoto,
            blob: file,
            mime,
            comentario,
            orden,
          });
          return { encolada: true };
        }
      }

      // Offline con un LOTO ya real (conexión caída después): encolar por id real.
      await enqueueAttachment({
        kind: "loto",
        idObra: obraId,
        clientUuid: null,
        serverId: idLoto,
        blob: file,
        mime,
        comentario,
        orden,
      });
      return { encolada: true };
    },
    // Solo la subida ONLINE cambia el servidor: invalida la ficha (num_fotos) y la lista de fotos.
    // Las encoladas no tocan el servidor todavía (el runner reconcilia la ficha al subirlas).
    onSuccess: (data, { idLoto }) => {
      if (!data.encolada) {
        void queryClient.invalidateQueries({ queryKey: [...lotosQueryKey(obraId), idLoto] });
      }
    },
  });
}

/**
 * useBorrarFotoLoto borra una foto del registro fotográfico de un LOTO (#212). El backend solo lo
 * permite a ADMIN/ADMIN_OBRA (la UI oculta la acción a los demás; el servidor es el guardián real).
 * Tras borrar invalida la ficha (num_fotos) y su lista de fotos.
 */
export function useBorrarFotoLoto(obraId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ idLoto, idFoto }: { idLoto: number; idFoto: number }) =>
      api.del<void>(`/obras/${obraId}/lotos/${idLoto}/fotos/${idFoto}`),
    onSuccess: (_data, { idLoto }) => {
      void queryClient.invalidateQueries({ queryKey: [...lotosQueryKey(obraId), idLoto] });
    },
  });
}
